import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Logo from "@/components/Logo";
import ScanResults from "@/components/ScanResults";
import AnimatedBackground from "@/components/AnimatedBackground";
import type { VisibilityAnalysis } from "@/lib/api/ai-visibility";
import type { PlatformResult } from "@/lib/api/platform-visibility";
import type { GoogleSearchResult } from "@/lib/api/google-search-visibility";
import { supabase } from "@/integrations/supabase/client";

// Illustrative sample data for SEO audit crawlers. Not a real scan.
const nikeAnalysis: VisibilityAnalysis = {
  visibilityScore: 70,
  keyFindings: [
    "Consistently recommended by ChatGPT for athletic footwear queries",
    "Ranks in Google's top 3 for the target keyword",
    "Featured in product comparison discussions with Adidas and Puma",
    "AI models reference Nike's innovation and technology leadership"
  ],
  competitorMentions: [
    { name: "Adidas", context: "Primary competitor in athletic footwear" },
    { name: "Puma", context: "Referenced in performance running category" },
    { name: "New Balance", context: "Mentioned for lifestyle sneaker alternatives" }
  ],
  recommendations: [
    "Continue building authoritative content around product innovation",
    "Expand presence in AI training datasets through structured data",
    "Create more FAQ-style content for AI retrieval optimization"
  ],
  summary: "Illustrative sample. Nike is shown here with strong visibility on the two platforms AILens scans, ChatGPT and Google Search."
};

const nikePlatformResults: PlatformResult[] = [
  {
    platform: "ChatGPT",
    found: true,
    context: "Nike is consistently recommended as a top choice for running shoes and athletic wear",
    confidence: "high",
    competitors: ["Adidas", "Puma", "New Balance"]
  }
];

const nikeGoogleResult: GoogleSearchResult = {
  success: true,
  brandName: "Nike",
  targetKeyword: "best running shoes",
  platform: "Google Search",
  found: true,
  position: 1,
  context: "Nike.com ranks #1 for 'best running shoes' with strong domain authority",
  confidence: "high",
  topCompetitors: ["Adidas", "Brooks", "ASICS"],
  domainVerified: true,
  spellingCorrected: false,
  suggestedQuery: undefined
};

const AuditCrawlPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isValidating, setIsValidating] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [errorCode, setErrorCode] = useState<number | null>(null);

  useEffect(() => {
    const validateAccess = async () => {
      const key = searchParams.get("key");
      
      // No key provided - show 404
      if (!key) {
        setErrorCode(404);
        setIsValidating(false);
        return;
      }

      try {
        // Validate key and check rate limit via edge function
        const { data, error } = await supabase.functions.invoke("audit-crawl-validate", {
          body: { key }
        });

        if (error) {
          console.error("Validation error:", error);
          setErrorCode(404);
          setIsValidating(false);
          return;
        }

        if (data?.valid === true) {
          setIsAuthorized(true);
        } else if (data?.rateLimited) {
          setErrorCode(429);
        } else {
          setErrorCode(404);
        }
      } catch (err) {
        console.error("Validation failed:", err);
        setErrorCode(404);
      }
      
      setIsValidating(false);
    };

    validateAccess();
  }, [searchParams]);

  // Show loading while validating
  if (isValidating) {
    return (
      <main className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Validating access...</div>
      </main>
    );
  }

  // Show 404 for invalid/missing key
  if (errorCode === 404) {
    return (
      <main className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <h1 className="text-4xl font-bold text-foreground">404</h1>
        <p className="text-muted-foreground">Page not found</p>
        <a href="/" className="text-primary hover:underline">Go to homepage</a>
      </main>
    );
  }

  // Show 429 for rate limit exceeded
  if (errorCode === 429) {
    return (
      <main className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <h1 className="text-4xl font-bold text-foreground">429</h1>
        <p className="text-muted-foreground">Too many requests. Please try again later.</p>
        <a href="/" className="text-primary hover:underline">Go to homepage</a>
      </main>
    );
  }

  // Authorized - show static results page
  if (!isAuthorized) {
    return null;
  }

  return (
    <>
      {/* SEO blocking meta tags - prevent indexing */}
      <Helmet>
        <title>AILens Audit Crawl: Static Demo Scan</title>
        <meta name="description" content="Static demo of an AILens AI visibility scan, served for SEO audit crawlers. Shows ChatGPT and Google scoring on a fixed sample brand." />
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
        <meta property="og:title" content="AILens Audit Crawl: Demo Scan" />
        <meta property="og:description" content="Static demo of an AI visibility scan for SEO audit crawlers." />
        <meta property="og:url" content="https://ai-lens.app/audit-crawl" />
      </Helmet>

      <main className="min-h-screen bg-background flex flex-col items-center px-4 py-8 pb-24 md:py-12 md:pb-12 relative overflow-hidden">
        {/* Animated background */}
        <AnimatedBackground />
        
        <div className="relative z-10 flex flex-col items-center gap-6 w-full">
          {/* Logo - clickable to go home */}
          <a href="/" className="hover:opacity-80 transition-opacity">
            <Logo />
          </a>

          {/* Illustrative-data banner */}
          <div className="w-full max-w-5xl border-2 border-warning bg-warning/10 px-4 py-3 text-center">
            <p className="text-sm font-semibold text-foreground">
              Illustrative sample data for crawler testing. Not a real scan.
            </p>
          </div>

          {/* Static Results for Nike demo */}
          <ScanResults 
            analysis={nikeAnalysis} 
            brandName="Nike"
            keyword="best running shoes"
            platformResults={nikePlatformResults}
            googleResult={nikeGoogleResult}
            websiteUrl="https://nike.com"
            chatGptStatus="success"
            googleStatus="success"
            isFromCache={true}
          />

          {/* Footer text */}
          <p className="text-muted-foreground text-sm text-center animate-fade-in mt-8" style={{ animationDelay: "0.5s" }}>
            AILens © 2026 •{" "}
            <a href="/privacy" className="hover:text-primary transition-colors">Privacy Policy</a>
            {" "}•{" "}
            <a href="/terms" className="hover:text-primary transition-colors">Terms of Service</a>
          </p>
        </div>
      </main>
    </>
  );
};

export default AuditCrawlPage;
